import { useAuth } from '@clerk/react-router';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { UserContext } from '~/app';
import { LoadingSpinner } from '~/components';
import { authDebug, summarizeUrl } from './authDebug';
import { resolvePostAuthDestination, type PostAuthRole } from './postAuthRedirect';
import {
  CLERK_REDIRECT_URL_PARAM,
  OAUTH_COMPLETION_URL_PARAM,
  RETURN_TO_PARAM,
  buildSignInPathWithReturnTo,
  sanitizeOAuthCompletionUrl,
  sanitizeReturnTo,
} from './returnTo';
import { paths } from './routes';

type OAuthCompletion = {
  externalUrl: string | null;
  returnTo: string | null;
};

const resolveOAuthCompletion = (raw: string | null): OAuthCompletion | null => {
  const completionUrl = sanitizeOAuthCompletionUrl(raw, window.location.origin);
  if (!completionUrl) return null;

  const url = new URL(completionUrl, window.location.origin);
  if (url.origin !== window.location.origin) {
    return { externalUrl: url.toString(), returnTo: null };
  }

  if (url.pathname === paths.publicAuth.authComplete) {
    return {
      externalUrl: null,
      returnTo: sanitizeReturnTo(url.searchParams.get(RETURN_TO_PARAM), window.location.origin),
    };
  }

  return {
    externalUrl: null,
    returnTo: sanitizeReturnTo(`${url.pathname}${url.search}${url.hash}`, window.location.origin),
  };
};

const buildSignedOutFallbackPath = (
  returnTo: string | null,
  externalCompletionUrl: string | null
): string => {
  if (externalCompletionUrl) {
    const params = new URLSearchParams({
      [CLERK_REDIRECT_URL_PARAM]: externalCompletionUrl,
    });
    return `${paths.publicAuth.signIn}?${params.toString()}`;
  }

  if (!returnTo) return paths.publicAuth.signIn;

  const target = new URL(returnTo, window.location.origin);
  return buildSignInPathWithReturnTo(target.pathname, target.search, target.hash);
};

const getRole = ({
  isAuthLoading,
  isProvider,
  isEmployer,
  isImpersonating,
  isProviderRegistration,
  isEmployerRegistration,
  needsRoleSelection,
}: {
  isAuthLoading: boolean;
  isProvider: boolean;
  isEmployer: boolean;
  isImpersonating: boolean;
  isProviderRegistration: boolean;
  isEmployerRegistration: boolean;
  needsRoleSelection: boolean;
}): PostAuthRole => {
  if (isAuthLoading) return 'loading';
  if (isProvider) return 'provider';
  if (isImpersonating) return 'impersonating';
  if (isEmployer) return 'employer';
  if (isProviderRegistration) return 'registration-provider';
  if (isEmployerRegistration) return 'registration-employer';
  if (needsRoleSelection) return 'choose-role';
  return 'unknown';
};

export const AuthCompletePage = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isProvider = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const isEmployer = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_EMPLOYER' })
  );
  const isImpersonating = UserContext.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const impersonatingProviderId = UserContext.useSelector(
    (state) => state.context.impersonatingProviderId
  );
  const impersonatingProviderName = UserContext.useSelector(
    (state) => state.context.impersonatingProviderName
  );
  const isProviderRegistration = UserContext.useSelector((state) =>
    state.matches('$_PROVIDER_REGISTRATION')
  );
  const isEmployerRegistration = UserContext.useSelector((state) =>
    state.matches('$_EMPLOYER_REGISTRATION')
  );
  const needsRoleSelection = UserContext.useSelector((state) => state.matches('$_CHOOSE_ROLE'));
  const isAuthLoading = UserContext.useSelector(
    (state) => state.matches('$_AUTHENTICATING') || state.matches('$_FETCHING_USER')
  );

  const returnTo = useMemo(
    () =>
      sanitizeReturnTo(
        new URLSearchParams(location.search).get(RETURN_TO_PARAM),
        window.location.origin
      ),
    [location.search]
  );
  const oauthCompletion = useMemo(
    () =>
      resolveOAuthCompletion(new URLSearchParams(location.search).get(OAUTH_COMPLETION_URL_PARAM)),
    [location.search]
  );
  const effectiveReturnTo = oauthCompletion ? oauthCompletion.returnTo : returnTo;
  const role = getRole({
    isAuthLoading: !isLoaded || !isSignedIn || isAuthLoading,
    isProvider,
    isEmployer,
    isImpersonating,
    isProviderRegistration,
    isEmployerRegistration,
    needsRoleSelection,
  });
  const destination = useMemo(
    () =>
      resolvePostAuthDestination({
        role,
        hostname: window.location.hostname,
        returnTo: effectiveReturnTo,
        impersonation:
          impersonatingProviderId != null
            ? {
                providerId: impersonatingProviderId,
                providerName: impersonatingProviderName,
              }
            : undefined,
      }),
    [effectiveReturnTo, impersonatingProviderId, impersonatingProviderName, role]
  );

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      const signInPath = buildSignedOutFallbackPath(
        effectiveReturnTo,
        oauthCompletion?.externalUrl ?? null
      );
      authDebug('auth-complete.signed_out_redirect', {
        returnTo: effectiveReturnTo,
        target: signInPath,
      });
      navigate(signInPath, { replace: true });
      return;
    }

    if (role === 'loading') return;

    if (oauthCompletion?.externalUrl) {
      authDebug('auth-complete.oauth_external_destination', {
        target: summarizeUrl(oauthCompletion.externalUrl),
      });
      window.location.assign(oauthCompletion.externalUrl);
      return;
    }

    authDebug('auth-complete.destination', {
      role,
      returnTo: effectiveReturnTo,
      destination:
        destination.type === 'external'
          ? { type: destination.type, url: summarizeUrl(destination.url) }
          : destination,
    });

    if (destination.type === 'external') {
      window.location.assign(destination.url);
      return;
    }

    if (destination.type === 'local') {
      navigate(destination.path, { replace: true });
    }
  }, [destination, effectiveReturnTo, isLoaded, isSignedIn, navigate, oauthCompletion, role]);

  return <LoadingSpinner />;
};
